import { CartLinesUpdateEvent } from '@shopify/events';

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';

    this.track = this.querySelector('[data-recommendation-track]');
    this.productId = this.dataset.productId;
    this.limit = Number(this.dataset.limit || 6);

    this.previousButton = this.querySelector('[data-recommendation-prev]');
    this.nextButton = this.querySelector('[data-recommendation-next]');

    this.previousButton?.addEventListener('click', () => {
      this.track?.scrollBy({
        left: -(this.track.clientWidth * 0.8),
        behavior: 'smooth',
      });
    });

    this.nextButton?.addEventListener('click', () => {
      this.track?.scrollBy({
        left: this.track.clientWidth * 0.8,
        behavior: 'smooth',
      });
    });

    this.loadRecommendations();
  }

  async loadRecommendations() {
    if (!this.productId || !this.track) return;

    try {
      const root = window.Shopify?.routes?.root || '/';

      let url =
        `${root}recommendations/products.json` +
        `?product_id=${encodeURIComponent(this.productId)}` +
        `&limit=${this.limit}` +
        `&intent=complementary`;

      let response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Recommendation request failed');
      }

      let data = await response.json();
      let products = data.products || [];

      /*
       * If complementary recommendations aren't configured,
       * fall back to Shopify's related-product recommendations.
       */
      if (!products.length) {
        url =
          `${root}recommendations/products.json` +
          `?product_id=${encodeURIComponent(this.productId)}` +
          `&limit=${this.limit}` +
          `&intent=related`;

        response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          data = await response.json();
          products = data.products || [];
        }
      }

      if (!products.length) {
        this.remove();
        return;
      }

      /*
       * Get the current cart so we don't recommend
       * something that is already in the cart.
       */
      const cartResponse = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      let cart = null;

      if (cartResponse.ok) {
        cart = await cartResponse.json();
      }

      const cartProductIds = new Set(
        (cart?.items || []).map((item) => Number(item.product_id))
      );

      products = products
        .filter((product) => product.available)
        .filter((product) => !cartProductIds.has(Number(product.id)))
        .slice(0, this.limit);

      if (!products.length) {
        this.remove();
        return;
      }

      this.renderProducts(products);
    } catch (error) {
      console.warn(
        '[cart-recommendations]',
        error
      );

      this.remove();
    }
  }

  renderProducts(products) {
    this.track.innerHTML = '';

    products.forEach((product) => {
      const card = document.createElement('article');

      card.className =
        'cart-drawer-recommendations__card';

      const image =
        product.featured_image ||
        product.images?.[0] ||
        '';

      const price = this.formatMoney(product.price);

      const compareAt =
        product.compare_at_price &&
        Number(product.compare_at_price) > Number(product.price)
          ? this.formatMoney(product.compare_at_price)
          : '';

      /*
       * Products with multiple variants should go
       * to the product page so customers can select
       * size/color/etc. safely.
       */
      const availableVariant =
        product.variants?.find(
          (variant) => variant.available
        );

      const hasMultipleVariants =
        (product.variants || []).length > 1;

      card.innerHTML = `
        <a
          class="cart-drawer-recommendations__image-link"
          href="${this.escapeAttribute(product.url)}"
        >
          ${
            image
              ? `
                <img
                  class="cart-drawer-recommendations__image"
                  src="${this.escapeAttribute(image)}"
                  alt="${this.escapeAttribute(product.title)}"
                  loading="lazy"
                  width="240"
                  height="240"
                >
              `
              : ''
          }
        </a>

        <div class="cart-drawer-recommendations__info">
          <a
            class="cart-drawer-recommendations__product-title"
            href="${this.escapeAttribute(product.url)}"
          >
            ${this.escapeHTML(product.title)}
          </a>

          <div class="cart-drawer-recommendations__price">
            <span>${price}</span>
            ${
              compareAt
                ? `<s>${compareAt}</s>`
                : ''
            }
          </div>

          ${
            hasMultipleVariants
              ? `
                <a
                  class="cart-drawer-recommendations__button"
                  href="${this.escapeAttribute(product.url)}"
                >
                  VIEW OPTIONS
                </a>
              `
              : `
                <button
                  type="button"
                  class="cart-drawer-recommendations__button"
                  data-recommendation-add
                  data-variant-id="${availableVariant?.id || ''}"
                  ${!availableVariant ? 'disabled' : ''}
                >
                  ADD
                </button>
              `
          }
        </div>
      `;

      const addButton =
        card.querySelector(
          '[data-recommendation-add]'
        );

      if (addButton) {
        addButton.addEventListener(
          'click',
          () => {
            this.addToCart(
              addButton,
              Number(addButton.dataset.variantId)
            );
          }
        );
      }

      this.track.appendChild(card);
    });

    this.updateArrowVisibility();
  }

  async addToCart(button, variantId) {
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;

    button.disabled = true;
    button.textContent = 'ADDING…';

    /*
     * Horizon's cart-items-component listens for this
     * standard Shopify event and updates the existing
     * cart drawer when the promise resolves.
     */
    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const sectionIds = [];

    cartItemsComponents.forEach((component) => {
      if (
        component instanceof HTMLElement &&
        component.dataset.sectionId
      ) {
        sectionIds.push(
          component.dataset.sectionId
        );
      }
    });

    const sections = [
      ...new Set(sectionIds),
    ].join(',');

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [
          {
            merchandiseId: String(variantId),
            quantity: 1,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
            sections,
            sections_url: window.location.pathname,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(
          data.description ||
          data.message ||
          'Unable to add product'
        );
      }

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            data
          ),
        detail: {
          sections: data.sections,
          items: data.items,
          source:
            'cart-drawer-recommendations',
          itemCount: data.item_count,
          didError: false,
        },
      });

      button.textContent = 'ADDED';

      /*
       * Horizon will morph the drawer here.
       * The recommendation section will therefore
       * refresh automatically as well.
       */
    } catch (error) {
      console.error(
        '[cart-recommendations]',
        error
      );

      deferredEventPromise.reject(error);

      button.disabled = false;
      button.textContent = originalText;
    }
  }

  formatMoney(cents) {
    const currency =
      window.Shopify?.currency?.active || 'INR';

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-IN',
        {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }
      ).format(Number(cents) / 100);
    } catch {
      return `₹${(
        Number(cents) / 100
      ).toFixed(0)}`;
    }
  }

  updateArrowVisibility() {
    if (!this.track) return;

    const update = () => {
      const canScroll =
        this.track.scrollWidth >
        this.track.clientWidth + 5;

      this.previousButton?.toggleAttribute(
        'hidden',
        !canScroll
      );

      this.nextButton?.toggleAttribute(
        'hidden',
        !canScroll
      );
    };

    requestAnimationFrame(update);
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHTML(value)
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (
  !customElements.get(
    'cart-drawer-recommendations'
  )
) {
  customElements.define(
    'cart-drawer-recommendations',
    CartDrawerRecommendations
  );
}